import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { NEAREST, easeInOut, exhibitFovFor, fitDistance, fovFor, frameDistance, pivotAt, visibleRect } from './fit';
import { keepPointersHonest } from './pointerHygiene';

/**
 * The renderer, the camera and the controls. Replaceable: nothing in the
 * simulation knows this file exists.
 */
export interface Insets {
  top: number;
  right: number;
  bottom: number;
  /** Along the left edge: the exhibit's floating panel on a wider screen (M94); none otherwise. */
  left?: number;
}

export interface Scene {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  controls: OrbitControls;
  resize(): void;
  /**
   * Pixels along the top, right and bottom edges covered by the HUD (the top
   * bar; a phone's side panel or bottom sheet). The aquarium is centered and
   * fitted in what is left.
   */
  setInsets(insets: Insets): void;
  /** A new radius to keep on screen, when the aquarium drawn changes (a load at another site). */
  setFitRadius(radius: number): void;
  /**
   * How the camera looks (M94): the map's wide lens, turning freely about the box's middle; or the
   * exhibit's narrow one, kept to the tank's front and framed on a habitat (`frame`).
   */
  setLook(look: 'map' | 'exhibit'): void;
  /**
   * Looks at `frame.target` from `direction` (toward the camera), near enough that `frame.points` fill the
   * view; smoothly, over `TRANSITION_S`, when `smooth`, else at once. The frame is kept, and fitted again
   * when the screen's shape changes. The map's look ignores it.
   */
  frame(frame: { target: readonly [number, number, number]; points: readonly (readonly [number, number, number])[]; half?: number; tall?: number }, direction: readonly [number, number, number], smooth: boolean): void;
  /** Moves a transition on to `now` (ms, the frame's clock), and the camera with what it follows. */
  update(now: number): void;
  /**
   * Follows a point the wildlife card names (M97, Follow Wildlife): first moved to, near enough to see the animals
   * there, then kept in the view's middle as it moves, the player's own turn and zoom kept; null stops following.
   */
  follow(point: readonly [number, number, number] | null, spanUnits?: number): void;
  /** Where the camera stands and what it looks at now, and whether that is the player's own (turned, zoomed or following) or the last frame's (M104, for Watch One Day's Exit to give back). */
  view(): { position: [number, number, number]; target: [number, number, number]; own: boolean };
  /**
   * Moves the camera back to a view, smoothly, over `TRANSITION_S`: the player's own kept as theirs; the frame's fitted
   * again to the screen's shape now (M104's second review: a phone's watch bar can change the shape meanwhile).
   */
  moveTo(view: { position: readonly [number, number, number]; target: readonly [number, number, number]; own?: boolean }): void;
  /** Whether a move between frames is under way (M104: the ruler is kept in view only once it ends). */
  moving(): boolean;
}

/**
 * How near the camera comes to an animal it is asked to follow, scene units (JUDGED: at 3.5 a roi's sprite filled a
 * sixth of a computer's view; at 7, its school and its neighbors are seen), or farther where the animal's span, as
 * the caller gives it, would not fit across the canvas (M99's review: a whale, 2.4 units, cut off on an upright phone).
 */
const FOLLOW_UNITS = 7;
/** How fast the view catches up with what it follows, a share a second (JUDGED: smooth, never a lurch). */
const FOLLOW_RATE = 3;

/** How long the camera takes between two frames, seconds (JUDGED: a smooth move, the brief's "no sudden jumps", quick enough not to wait on). */
const TRANSITION_S = 1.4;
/** How far the exhibit's camera may turn from its front, radians either way, and how high and low it may look from, from straight down (JUDGED: it stays a tank seen through its front glass). */
const EXHIBIT_AZIMUTH = 0.95;
const EXHIBIT_POLAR: readonly [number, number] = [0.9, 1.72];

/**
 * @param fitRadius the radius of the sphere holding what must stay on screen;
 * the camera stands back far enough for it on any screen shape (`fit.ts`).
 */
export function createScene(canvas: HTMLCanvasElement, fitRadius: number): Scene {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x0b1a26);

  const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 500);
  // From the south-east and well above the surface: the shelf, the reefs and
  // the islands, where a sanctuary is, read first; the cutaway walls below.
  camera.position.set(12, 21, 16);

  const controls = new OrbitControls(camera, canvas);
  // The aquarium's center, so the fitted sphere is centered on screen.
  controls.target.set(0, 0, 0);
  controls.enableDamping = true;
  // Close enough to read a reef (`fit.ts`).
  controls.minDistance = NEAREST;
  controls.maxDistance = 60;
  // Zooming goes toward the pointer, as a map does, so a reef or an island under the
  // cursor (or between two fingers) is where the view closes in.
  controls.zoomToCursor = true;
  // OrbitControls closes in on the point under the pointer in the plane through its
  // target, which is the box's middle, 6 units below the islands: zoomed at an island,
  // the view slid past it onto the cliffs behind. So before a zoom the target is moved
  // along the line of sight to the depth of what is under the pointer. The camera and
  // its direction are unchanged, so the picture does not move; the turn then pivots there.
  const raycaster = new THREE.Raycaster();
  const pointer = new THREE.Vector2();
  const forward = new THREE.Vector3();
  const aimAt = (clientX: number, clientY: number): void => {
    // Not while a habitat's own scene is shown (M110): its controls are its own.
    if (!controls.enabled) return;
    const box = canvas.getBoundingClientRect();
    pointer.set(((clientX - box.left) / box.width) * 2 - 1, -((clientY - box.top) / box.height) * 2 + 1);
    raycaster.setFromCamera(pointer, camera);
    const hit = raycaster.intersectObjects(scene.children, true).find((h) => h.object instanceof THREE.Mesh);
    if (!hit) return;
    camera.getWorldDirection(forward);
    const pivot = pivotAt(camera.position.toArray(), forward.toArray(), hit.point.toArray(), controls.minDistance, controls.maxDistance);
    if (pivot) controls.target.set(...pivot);
  };
  canvas.addEventListener('wheel', (e) => aimAt(e.clientX, e.clientY), { capture: true, passive: true });
  const touches = new Map<number, [number, number]>();
  canvas.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch') return;
    touches.set(e.pointerId, [e.clientX, e.clientY]);
    // The second finger down starts a pinch: aimed between the two.
    if (touches.size === 2) {
      const [[ax, ay], [bx, by]] = [...touches.values()] as [[number, number], [number, number]];
      aimAt((ax + bx) / 2, (ay + by) / 2);
    }
  }, { capture: true });
  // Each finger where it is now, so a pinch begun after a turn is aimed between where the fingers are (M81's reviewer: the
  // first finger's starting point was kept).
  canvas.addEventListener('pointermove', (e) => { if (touches.has(e.pointerId)) touches.set(e.pointerId, [e.clientX, e.clientY]); }, { capture: true });
  const lift = (e: PointerEvent): void => { touches.delete(e.pointerId); };
  canvas.addEventListener('pointerup', lift);
  canvas.addEventListener('pointercancel', lift);
  // Touch: one finger turns the view, two pinch and pan, as OrbitControls does
  // by default. The page itself must not scroll or zoom under the gesture.
  canvas.style.touchAction = 'none';
  // A finger whose lifting the browser never reported retired at the next touch, so one finger is not read as two
  // (M115, `pointerHygiene.ts`): the whole tank's controls and the habitat scenes' alike, both on this canvas.
  keepPointersHonest(canvas);
  controls.maxPolarAngle = Math.PI * 0.62;

  // The sky's light; the sun's is the aquarium's own, from where it stands at noon (M34).
  scene.add(new THREE.HemisphereLight(0xcfe8ff, 0x0a2030, 1.2));

  let insets: Insets = { top: 0, right: 0, bottom: 0 };
  let look: 'map' | 'exhibit' = 'map';
  /** The exhibit's frame, kept to fit again on a new screen shape. */
  let framed: { target: readonly [number, number, number]; points: readonly (readonly [number, number, number])[]; direction: readonly [number, number, number]; half?: number; tall?: number } | null = null;
  /** A move between two frames under way: from and to, the camera's place and its target, when it began, and whether it ends on a view of the player's own (a watched day's give-back, M104), kept as theirs. */
  let moving: { from: THREE.Vector3; fromTarget: THREE.Vector3; to: THREE.Vector3; toTarget: THREE.Vector3; start: number | null; own?: boolean } | null = null;
  /** The point followed, and when the camera last caught up with it (ms). */
  let followed: THREE.Vector3 | null = null;
  /** Where a frame last put the camera: moved from there, it is the player's (`resize`). */
  let placedAt: THREE.Vector3 | null = null;
  // A player's own turn or zoom takes the camera from a move under way.
  controls.addEventListener('start', () => { if (controls.enabled) moving = null; });
  let lastShape = '';
  const resize = (): void => {
    const parent = canvas.parentElement ?? document.body;
    const w = Math.max(parent.clientWidth, 1);
    const h = Math.max(parent.clientHeight, 1);
    // What the HUD leaves of the canvas; the camera centered on it, in a larger virtual frame (`visibleRect`).
    const { width: vw, height: vh, fullWidth: fullW, fullHeight: fullH, offsetX, offsetY } = visibleRect(w, h, insets);
    renderer.setSize(w, h, false);
    // The field of view is chosen for the visible rectangle and widened to the
    // virtual frame it is a part of.
    const aspect = vw / vh;
    const visibleFov = look === 'exhibit' ? exhibitFovFor(aspect) : fovFor(aspect);
    const half = (visibleFov * Math.PI) / 360;
    camera.fov = (Math.atan(Math.tan(half) * (fullH / vh)) * 360) / Math.PI;
    camera.aspect = fullW / fullH;
    if (fullW !== w || fullH !== h) camera.setViewOffset(fullW, fullH, offsetX, offsetY, w, h);
    else camera.clearViewOffset();
    camera.updateProjectionMatrix();
    // Refit the distance when the visible shape changes, keeping the angle the
    // player has turned to, so a rotated phone still shows the whole aquarium:
    // about the box's middle again, where a zoom since M81 may have moved the pivot
    // (M81's reviewer: refitted about a pivot on a shore, a corner left the screen).
    const shape = `${Math.round(vw)}x${Math.round(vh)}`;
    if (shape !== lastShape && look === 'exhibit' && framed) {
      // The exhibit's habitat fitted again, from the way the player has turned it, eased there as a move between
      // habitats is (M94's review: put there at once, a panel shown or hidden jumped the view); not where the player
      // has zoomed or panned away from it, or follows an animal, their view kept (M96's review: Watch One Day's bar
      // took the camera back to the habitat).
      const first = lastShape === '';
      lastShape = shape;
      // A move back to the player's own view is theirs (M104's review: on a phone the watch bar's going re-framed it).
      const own = !first && (moving ? moving.own === true : followed !== null || !placedAt || camera.position.distanceTo(placedAt) > 1e-3);
      if (own) return;
      const turned = camera.position.clone().sub(controls.target);
      const direction = moving || first || turned.lengthSq() === 0 ? framed.direction : (turned.normalize().toArray() as [number, number, number]);
      const at = place({ ...framed, direction }, aspect, visibleFov);
      if (first) {
        moving = null;
        controls.target.copy(at.target);
        camera.position.copy(at.position);
        controls.update();
        placedAt = camera.position.clone();
      } else moving = { from: camera.position.clone(), fromTarget: controls.target.clone(), to: at.position, toTarget: at.target, start: null };
    } else if (shape !== lastShape) {
      lastShape = shape;
      controls.target.set(0, 0, 0);
      const toward = camera.position.clone().sub(controls.target);
      const distance = fitDistance(fitRadius, aspect, visibleFov);
      const offset = toward.setLength(distance);
      camera.position.copy(controls.target).add(offset);
      controls.maxDistance = Math.max(60, distance * 2);
      controls.update();
    }
  };
  /** The camera's place and target for a frame on this screen: back along its direction until its points fill the view, and a little more. */
  const place = (f: NonNullable<typeof framed>, aspect: number, fov: number): { position: THREE.Vector3; target: THREE.Vector3 } => {
    const target = new THREE.Vector3(...f.target);
    const toward = new THREE.Vector3(...f.direction).normalize();
    const distance = frameDistance(f, [toward.x, toward.y, toward.z], aspect, fov);
    controls.maxDistance = Math.max(controls.maxDistance, distance * 2);
    return { position: target.clone().add(toward.multiplyScalar(distance)), target };
  };
  /** The visible rectangle's shape and its field of view now. */
  const visible = (): { aspect: number; fov: number } => {
    const parent = canvas.parentElement ?? document.body;
    const rect = visibleRect(Math.max(parent.clientWidth, 1), Math.max(parent.clientHeight, 1), insets);
    const aspect = rect.width / rect.height;
    return { aspect, fov: look === 'exhibit' ? exhibitFovFor(aspect) : fovFor(aspect) };
  };
  window.addEventListener('resize', resize);
  resize();

  const setLook = (next: 'map' | 'exhibit'): void => {
    look = next;
    moving = null;
    if (look === 'exhibit') {
      controls.minAzimuthAngle = -EXHIBIT_AZIMUTH;
      controls.maxAzimuthAngle = EXHIBIT_AZIMUTH;
      [controls.minPolarAngle, controls.maxPolarAngle] = EXHIBIT_POLAR;
    } else {
      framed = null;
      controls.minAzimuthAngle = -Infinity;
      controls.maxAzimuthAngle = Infinity;
      controls.minPolarAngle = 0;
      controls.maxPolarAngle = Math.PI * 0.62;
    }
    lastShape = '';
    resize();
  };

  const frame = (f: { target: readonly [number, number, number]; points: readonly (readonly [number, number, number])[]; half?: number; tall?: number }, direction: readonly [number, number, number], smooth: boolean): void => {
    if (look !== 'exhibit') return;
    followed = null;
    framed = { ...f, direction };
    const { aspect, fov } = visible();
    const at = place(framed, aspect, fov);
    if (!smooth) {
      moving = null;
      controls.target.copy(at.target);
      camera.position.copy(at.position);
      controls.update();
      placedAt = camera.position.clone();
      return;
    }
    moving = { from: camera.position.clone(), fromTarget: controls.target.clone(), to: at.position, toTarget: at.target, start: null };
  };

  let lastUpdate: number | null = null;
  const follow = (point: readonly [number, number, number] | null, spanUnits = 0): void => {
    const first = followed === null && point !== null;
    followed = point ? new THREE.Vector3(...point) : null;
    if (!first || !followed) return;
    // First moved there, near enough to see it whole, from the way the camera looks now.
    const across = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) * camera.aspect;
    const units = Math.max(FOLLOW_UNITS, across > 0 ? spanUnits / across : 0);
    const toward = camera.position.clone().sub(controls.target).normalize();
    moving = { from: camera.position.clone(), fromTarget: controls.target.clone(), to: followed.clone().add(toward.multiplyScalar(units)), toTarget: followed.clone(), start: null };
  };

  const update = (now: number): void => {
    const dt = lastUpdate === null ? 0 : Math.max(0, now - lastUpdate) / 1000;
    lastUpdate = now;
    if (followed && !moving) {
      // Kept in the middle as it moves: the target and the camera shifted alike toward it.
      const shift = followed.clone().sub(controls.target).multiplyScalar(1 - Math.exp(-FOLLOW_RATE * dt));
      controls.target.add(shift);
      camera.position.add(shift);
    }
    if (!moving) return;
    if (followed) {
      // A move to what is followed ends where it is now.
      moving.toTarget.copy(followed);
    }
    moving.start ??= now;
    const t = Math.min(1, (now - moving.start) / (TRANSITION_S * 1000));
    const k = easeInOut(t);
    camera.position.lerpVectors(moving.from, moving.to, k);
    controls.target.lerpVectors(moving.fromTarget, moving.toTarget, k);
    if (t >= 1) {
      // A view of the player's own given back stays theirs: not fitted again to the frame at the screen's next shape.
      placedAt = moving.own ? null : camera.position.clone();
      moving = null;
    }
  };

  const setFitRadius = (radius: number): void => {
    fitRadius = radius;
    lastShape = '';
    resize();
  };

  const setInsets = (next: Insets): void => {
    if (Math.abs(next.top - insets.top) < 1 && Math.abs(next.right - insets.right) < 1
      && Math.abs(next.bottom - insets.bottom) < 1 && Math.abs((next.left ?? 0) - (insets.left ?? 0)) < 1) return;
    insets = next;
    resize();
  };

  const view = (): { position: [number, number, number]; target: [number, number, number]; own: boolean } => ({
    position: camera.position.toArray() as [number, number, number],
    target: controls.target.toArray() as [number, number, number],
    own: followed !== null || moving !== null || !placedAt || camera.position.distanceTo(placedAt) > 1e-3,
  });
  const moveTo = (to: { position: readonly [number, number, number]; target: readonly [number, number, number]; own?: boolean }): void => {
    followed = null;
    if (to.own === false && framed && look === 'exhibit') {
      const { aspect, fov } = visible();
      const at = place(framed, aspect, fov);
      moving = { from: camera.position.clone(), fromTarget: controls.target.clone(), to: at.position, toTarget: at.target, start: null };
      return;
    }
    moving = { from: camera.position.clone(), fromTarget: controls.target.clone(), to: new THREE.Vector3(...to.position), toTarget: new THREE.Vector3(...to.target), start: null, own: true };
  };

  return { renderer, scene, camera, controls, resize, setInsets, setFitRadius, setLook, frame, update, follow, view, moveTo, moving: () => moving !== null };
}
